import classNames from 'classnames';
import { cardIcons } from '../../../constants/icons';
import Badge from '../../Atoms/Badge';
import Title from '../../Atoms/Title';
import styles from './CardVisual.module.css';

export type IconProps =
  | 'windmill'
  | 'plug'
  | 'code'
  | 'conveyor'
  | 'gear'
  | 'helmet'
  | 'chart'
  | 'clipboard'
  | 'robot'
  | 'lightning'
  | 'gears'
  | undefined;

export interface Props {
  title: string;
  href?: string;
  color?: 'indigo' | 'yellow';
  className?: string;
  bgImage?: string;
  icon?: IconProps;
  badge?: string;
  onClick?: () => void;
}

interface CardVisualProps {
  cards: Props[];
  color?: 'indigo' | 'yellow';
}

function CardVisual({
  href = '#!',
  title,
  className,
  color = 'indigo',
  bgImage,
  icon,
  badge,
  onClick
}: Props) {
  return (
    <a
      href={href}
      onClick={onClick}
      className={classNames([
        'group relative block overflow-hidden rounded-xl p-5 text-white shadow-lg transition md:px-6 md:py-10',
        styles.CardVisual,
        { 'bg-sl-indigo': color === 'indigo' },
        { 'bg-[#FFD47E]': color === 'yellow' },
        className
      ])}
      style={styles}
    >
      {bgImage && (
        <span
          className={classNames([
            'absolute left-0 top-0 z-0 h-full w-full bg-cover bg-center mix-blend-multiply brightness-125 grayscale transition duration-300',
            {
              'opacity-70': color === 'indigo'
            },
            {
              'opacity-100': color === 'yellow'
            }
          ])}
          style={{ backgroundImage: `url(${bgImage})` }}
        />
      )}
      <span className="absolute left-0 top-0 z-10 h-full w-full rounded-xl bg-sl-blue opacity-10 mix-blend-multiply transition duration-300 group-hover:opacity-75 group-hover:mix-blend-normal" />

      <span className="relative z-20 flex h-full w-full flex-row items-center gap-7 md:flex-col md:justify-center md:gap-5 md:text-center">
        {icon && (
          <span className="inline-flex h-10 w-12 justify-center md:mb-auto md:w-auto md:basis-0">
            {cardIcons[icon]}
          </span>
        )}
        <span className="flex flex-col items-start gap-3 md:items-center">
          <Title
            as="h2"
            color="white"
            weight={900}
            size={5}
            className="tracking-tighter"
          >
            {title}
          </Title>
          {badge && (
            <Badge
              text={badge}
              color="white"
              className={classNames([
                'mt-2.5 px-[1.5em] duration-300',
                color === 'indigo' &&
                  'group-hover:bg-sl-indigo group-hover:text-white',
                color === 'yellow' &&
                  'group-hover:bg-sl-yellow '
              ])}
            />
          )}
        </span>
      </span>
    </a>
  );
}

function CardVisualList({ cards, color }: CardVisualProps) {
  return (
    <div className="grid grid-cols-1 gap-7 md:grid-cols-2 lg:grid-cols-4">
      {cards.map((card) => (
        <CardVisual
          key={cards.indexOf(card)}
          color={color}
          {...card}
        />
      ))}
    </div>
  );
}

export { CardVisualList };
export default CardVisual;
